import {
  HHAlert,
  HHButton,
  HHFormControlLabel,
  HHStack,
  HHSwitch,
  HHTypography,
} from '@hinge-health/react-component-library';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { StepNav } from '../components/layout/step-nav';
import { ContractTreeView } from '../components/summary/contract-tree-view';
import { useStepHeader } from '../context/wizard-header.context';
import { useWizardState } from '../context/wizard-state.context';
import { useCommitOrchestrator } from '../hooks/use-commit-orchestrator';
import { buildContractTree } from '../utils/build-contract-tree';

const monoText = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: 12,
};

const STATUS_COLOR: Record<string, string> = {
  pending: 'rgba(0,0,0,0.4)',
  success: '#198754',
  error: '#dc3545',
  simulated: '#6c757d',
};

const STATUS_GLYPH: Record<string, string> = {
  pending: '○',
  success: '✓',
  error: '✗',
  simulated: '◌',
};

export const ReviewStep = (): JSX.Element => {
  const { state, setState } = useWizardState();
  const navigate = useNavigate();
  const orchestrator = useCommitOrchestrator();
  const [confirmLiveOpen, setConfirmLiveOpen] = useState(false);

  useStepHeader({
    description:
      'Final review. Commit executes the ordered plan with idempotency keys. Halts on error and offers Resume.',
  });
  const tree = useMemo(
    () => buildContractTree({ state, stage: 'review' }),
    [state],
  );
  const { plan, log, running, done, resumeFromStep } = orchestrator.state;

  // If a commit kicks off while the confirm dialog is still on screen, drop
  // it so the operator can't accidentally re-confirm against a now-stale
  // mode toggle. Must run before the early-return so hook order stays stable.
  useEffect(() => {
    if (running && confirmLiveOpen) setConfirmLiveOpen(false);
  }, [running, confirmLiveOpen]);

  if (state.input == null) return <Navigate to="../intake" replace />;

  const logByStep = new Map(log.map(l => [l.step, l]));
  const simulateOnly = state.commit.simulateOnly;

  const handleModeToggle = (nextChecked: boolean): void => {
    // nextChecked === true means the operator wants Live (simulateOnly=false).
    // Gate that path behind the confirm dialog; flipping back to Simulate is
    // the safe direction and applies immediately.
    if (nextChecked) {
      setConfirmLiveOpen(true);
      return;
    }
    setState(s => ({ ...s, commit: { ...s.commit, simulateOnly: true } }));
  };

  const confirmLive = (): void => {
    setState(s => ({ ...s, commit: { ...s.commit, simulateOnly: false } }));
    setConfirmLiveOpen(false);
  };

  return (
    <HHStack spacing={3}>
      {resumeFromStep != null && (
        <HHAlert hhVariant="outlined" severity="error">
          <HHStack spacing={1}>
            <HHTypography hhVariant="subtitle2">
              Commit halted at <code>{resumeFromStep}</code>
            </HHTypography>
            <HHButton
              hhVariant="variant-bypass"
              variant="contained"
              color="primary"
              disabled={running}
              startIcon={
                running ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
              onClick={(): void => {
                void orchestrator.resume();
              }}
              sx={{ alignSelf: 'flex-start' }}
            >
              Resume from here
            </HHButton>
          </HHStack>
        </HHAlert>
      )}

      <HHStack spacing={1}>
        <HHTypography hhVariant="subtitle1">
          What we have & what will change
        </HHTypography>
        <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
          The contract as a tree. Each Contract subtree owns its Customer
          Account and Merchant + Locations + Practitioners. Items, Bundle, Fee
          Schedule, and CSPs sit under Shared resources.
        </HHTypography>
        <ContractTreeView tree={tree} />
      </HHStack>

      <HHStack spacing={1}>
        <HHTypography hhVariant="subtitle1">Technical plan</HHTypography>
        <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
          Ordered write list with idempotency keys and raw responses. Use this
          when a step fails — it links back to the resume point.
        </HHTypography>
        {plan.map(action => {
          const entry = logByStep.get(action.step);
          const status = entry?.status ?? 'pending';
          const glyph = action.isWrite ? STATUS_GLYPH[status] : '↻';
          const color = action.isWrite
            ? STATUS_COLOR[status]
            : STATUS_COLOR.pending;
          const isFailed = status === 'error';
          const isResumeTarget = isFailed && resumeFromStep === action.step;
          return (
            <Accordion
              key={action.step}
              variant="outlined"
              disableGutters
              defaultExpanded={isResumeTarget}
              sx={{ borderLeft: `3px solid ${color}` }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <HHStack
                  direction="row"
                  spacing={2}
                  alignItems="baseline"
                  sx={{ width: '100%' }}
                >
                  <span style={{ width: 16, color }}>{glyph}</span>
                  <HHTypography hhVariant="body2">
                    {action.description}
                  </HHTypography>
                  {entry?.error != null && (
                    <HHTypography
                      hhVariant="caption"
                      sx={{
                        flex: 1,
                        color: STATUS_COLOR.error,
                        textAlign: 'right',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {entry.error}
                    </HHTypography>
                  )}
                  {!action.isWrite && entry == null && (
                    <HHTypography
                      hhVariant="caption"
                      sx={{ flex: 1, opacity: 0.6, textAlign: 'right' }}
                    >
                      reuse
                    </HHTypography>
                  )}
                </HHStack>
              </AccordionSummary>
              <AccordionDetails>
                <HHStack spacing={1}>
                  <HHStack direction="row" spacing={2}>
                    <HHTypography
                      hhVariant="caption"
                      sx={{ width: 140, opacity: 0.7 }}
                    >
                      Step
                    </HHTypography>
                    <HHTypography hhVariant="caption" sx={monoText}>
                      {action.step}
                    </HHTypography>
                  </HHStack>
                  <HHStack direction="row" spacing={2}>
                    <HHTypography
                      hhVariant="caption"
                      sx={{ width: 140, opacity: 0.7 }}
                    >
                      Kind
                    </HHTypography>
                    <HHTypography hhVariant="caption" sx={monoText}>
                      {action.isWrite ? 'write' : 'reuse (no write)'}
                    </HHTypography>
                  </HHStack>
                  <HHStack direction="row" spacing={2}>
                    <HHTypography
                      hhVariant="caption"
                      sx={{ width: 140, opacity: 0.7 }}
                    >
                      Status
                    </HHTypography>
                    <HHTypography hhVariant="caption" sx={monoText}>
                      {action.isWrite ? status : 'reuse'}
                    </HHTypography>
                  </HHStack>
                  <HHStack direction="row" spacing={2}>
                    <HHTypography
                      hhVariant="caption"
                      sx={{ width: 140, opacity: 0.7 }}
                    >
                      Idempotency key
                    </HHTypography>
                    <HHTypography
                      hhVariant="caption"
                      sx={{ ...monoText, wordBreak: 'break-all' }}
                    >
                      {action.idempotencyKey}
                    </HHTypography>
                  </HHStack>
                  {entry?.response != null && (
                    <>
                      <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
                        Response
                      </HHTypography>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1,
                          ...monoText,
                          whiteSpace: 'pre-wrap',
                          maxHeight: 240,
                          overflow: 'auto',
                        }}
                      >
                        {typeof entry.response === 'string'
                          ? entry.response
                          : JSON.stringify(entry.response, null, 2)}
                      </Paper>
                    </>
                  )}
                  {entry?.error != null && (
                    <>
                      <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
                        Error
                      </HHTypography>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1,
                          ...monoText,
                          whiteSpace: 'pre-wrap',
                          color: STATUS_COLOR.error,
                        }}
                      >
                        {entry.error}
                      </Paper>
                    </>
                  )}
                  {isResumeTarget && (
                    <HHButton
                      hhVariant="variant-bypass"
                      variant="contained"
                      color="primary"
                      size="small"
                      disabled={running}
                      startIcon={
                        running ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : undefined
                      }
                      onClick={(): void => {
                        void orchestrator.resume();
                      }}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      Resume from here
                    </HHButton>
                  )}
                </HHStack>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </HHStack>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderColor: simulateOnly ? undefined : '#dc3545',
          backgroundColor: simulateOnly ? undefined : 'rgba(220,53,69,0.06)',
        }}
      >
        <HHStack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <HHStack spacing={0.5}>
            <HHTypography hhVariant="subtitle2">
              Commit mode: {simulateOnly ? 'Simulate (no writes)' : 'Live'}
            </HHTypography>
            <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
              {simulateOnly
                ? 'Commit will walk the plan but send zero POSTs. Flip the switch to enable real writes.'
                : 'Commit will issue real writes against the BFF. Flip the switch back to Simulate to disable.'}
            </HHTypography>
          </HHStack>
          <HHStack direction="row" spacing={1.5} alignItems="center">
            <HHFormControlLabel
              label={simulateOnly ? 'Enable live writes' : 'Live writes ON'}
              labelPlacement="start"
              control={
                <HHSwitch
                  hhVariant="variant-bypass"
                  checked={!simulateOnly}
                  onChange={(_e, checked): void => handleModeToggle(checked)}
                  disabled={running}
                />
              }
            />
            <HHButton
              hhVariant="variant-bypass"
              variant="contained"
              color={simulateOnly ? 'primary' : 'error'}
              disabled={running || done}
              startIcon={
                running ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
              onClick={(): void => {
                void orchestrator.commit();
              }}
            >
              {running
                ? simulateOnly
                  ? 'Simulating…'
                  : 'Committing…'
                : done
                  ? 'Committed ✓'
                  : simulateOnly
                    ? 'Simulate commit'
                    : 'Commit (live)'}
            </HHButton>
            {done && (
              <HHButton
                hhVariant="variant-bypass"
                variant="contained"
                color="success"
                onClick={(): void => navigate('../done')}
              >
                View results →
              </HHButton>
            )}
          </HHStack>
        </HHStack>
      </Paper>

      <StepNav
        id="review"
        canContinue={done}
        continueLabel="Continue →"
        disabledInfo={
          done
            ? undefined
            : running
              ? { message: 'Committing…', severity: 'info' }
              : {
                  message: simulateOnly
                    ? 'Currently in Simulate mode — no writes will be sent. Flip to Live to perform a real commit; Continue unlocks once a live commit succeeds.'
                    : 'Click Commit above to write everything. Continue unlocks once the commit succeeds.',
                  severity: 'info',
                }
        }
      />

      <Dialog
        open={confirmLiveOpen}
        onClose={(): void => setConfirmLiveOpen(false)}
      >
        <DialogTitle>Enable live writes?</DialogTitle>
        <DialogContent>
          <HHStack spacing={1}>
            <HHTypography hhVariant="body2">
              Switching to Live mode means the next Commit will issue real POSTs
              against the BFF and create entities in the Knowledge Graph,
              Customer Accounts, and Provider Network services.
            </HHTypography>
            <HHTypography hhVariant="body2">
              You can flip back to Simulate at any time before clicking Commit.
            </HHTypography>
          </HHStack>
        </DialogContent>
        <DialogActions>
          <HHButton
            hhVariant="variant-bypass"
            variant="outlined"
            color="primary"
            onClick={(): void => setConfirmLiveOpen(false)}
          >
            Cancel
          </HHButton>
          <HHButton
            hhVariant="variant-bypass"
            variant="contained"
            color="error"
            onClick={confirmLive}
          >
            Enable live writes
          </HHButton>
        </DialogActions>
      </Dialog>
    </HHStack>
  );
};
